import {
  clearQuizHistory as clearQuizHistoryApi,
  createQuizAttempt,
  createSimulationAttempt,
  generateClientUUID,
  getProgressDashboard,
  importLocalHistory,
  isUserConnected,
  updateResourceState,
} from './progressService';
import { readJson, readJsonArray, writeJson } from '../utils/storage';

const DASHBOARD_CACHE_PREFIX = 'progress_dashboard_cache_v1';
const MIGRATED_UUIDS_PREFIX = 'progress_migrated_uuids_v1';
const PENDING_ATTEMPTS_PREFIX = 'progress_pending_attempts_v1';
const PENDING_CLEAR_PREFIX = 'progress_pending_clear_v1';
const PENDING_RESOURCE_PREFIX = 'progress_pending_resources_v1';
const dashboardLoads = new Map();

function cacheKey(userId) {
  return `${DASHBOARD_CACHE_PREFIX}:${userId}`;
}

function migratedUuidsKey(userId) {
  return `${MIGRATED_UUIDS_PREFIX}:${userId}`;
}

function pendingAttemptsKey(userId) {
  return `${PENDING_ATTEMPTS_PREFIX}:${userId}`;
}

function pendingClearKey(userId) {
  return `${PENDING_CLEAR_PREFIX}:${userId}`;
}

function pendingResourceKey(userId) {
  return `${PENDING_RESOURCE_PREFIX}:${userId}`;
}

function resourceLocalKey(baseKey, userId) {
  return userId ? `${baseKey}:${userId}` : baseKey;
}

function shouldKeepPending(error) {
  const status = Number(error?.status);
  const code = error?.data?.code || error?.code;

  if (!Number.isInteger(status)) return true;
  if (status === 401 || status === 408 || status === 429 || status >= 500) {
    return true;
  }
  if (status === 403 && code === 'PREMIUM_REQUIRED') {
    return true;
  }
  return false;
}

function removePendingAttempt(userId, type, clientUuid) {
  if (!userId || !clientUuid) return;

  const key = pendingAttemptsKey(userId);
  const pending = readJsonArray(key);
  const next = pending.filter(
    (entry) =>
      !(
        entry?.type === type &&
        entry?.payload?.client_uuid === clientUuid
      )
  );

  if (next.length !== pending.length) {
    writeJson(key, next);
  }
}

export function queuePendingAttempt(userId, type, payload) {
  if (
    !userId ||
    !['quiz', 'simulation'].includes(type) ||
    !payload?.client_uuid
  ) {
    return false;
  }

  const key = pendingAttemptsKey(userId);
  const pending = readJsonArray(key).filter(
    (entry) =>
      !(
        entry?.type === type &&
        entry?.payload?.client_uuid === payload.client_uuid
      )
  );

  pending.push({
    type,
    payload,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  });

  return writeJson(key, pending.slice(-100));
}

async function submitAttempt(type, payload) {
  return type === 'quiz'
    ? createQuizAttempt(payload)
    : createSimulationAttempt(payload);
}

export async function submitProgressAttemptWithFallback(
  userId,
  type,
  payload
) {
  try {
    const result = await submitAttempt(type, payload);
    removePendingAttempt(userId, type, payload?.client_uuid);
    return { result, queued: false };
  } catch (error) {
    if (userId && shouldKeepPending(error)) {
      queuePendingAttempt(userId, type, payload);
      return { result: null, queued: true };
    }
    throw error;
  }
}

export async function flushPendingAttempts(userId) {
  if (!userId || !isUserConnected()) {
    return { processed: 0, synced: 0, kept: 0, dropped: 0 };
  }

  const key = pendingAttemptsKey(userId);
  const pending = readJsonArray(key);

  if (pending.length === 0) {
    return { processed: 0, synced: 0, kept: 0, dropped: 0 };
  }

  const kept = [];
  let synced = 0;
  let dropped = 0;

  for (const entry of pending) {
    if (
      !['quiz', 'simulation'].includes(entry?.type) ||
      !entry?.payload?.client_uuid
    ) {
      dropped += 1;
      continue;
    }

    try {
      await submitAttempt(entry.type, entry.payload);
      synced += 1;
    } catch (error) {
      if (shouldKeepPending(error)) {
        kept.push({
          ...entry,
          attempts: Number(entry.attempts || 0) + 1,
          lastAttemptAt: new Date().toISOString(),
        });
      } else {
        dropped += 1;
      }
    }
  }

  writeJson(key, kept.slice(-100));

  return {
    processed: pending.length,
    synced,
    kept: kept.length,
    dropped,
  };
}

function queuePendingResourceState(userId, resourceId, updates) {
  const key = pendingResourceKey(userId);
  const pending = readJsonArray(key);
  const numericId = Number(resourceId);
  const existing = pending.find(
    (entry) => Number(entry?.resource_id) === numericId
  );

  const next = pending.filter(
    (entry) => Number(entry?.resource_id) !== numericId
  );

  next.push({
    resource_id: numericId,
    updates: {
      ...(existing?.updates || {}),
      ...updates,
    },
    queuedAt: existing?.queuedAt || new Date().toISOString(),
    attempts: Number(existing?.attempts || 0),
  });

  writeJson(key, next.slice(-100));
}

export async function submitResourceStateWithFallback(
  userId,
  resourceId,
  updates
) {
  const numericId = Number(resourceId);
  if (
    !userId ||
    !Number.isInteger(numericId) ||
    numericId <= 0 ||
    !isUserConnected()
  ) {
    return { result: null, queued: false, localOnly: true };
  }

  try {
    const result = await updateResourceState(numericId, updates);
    const key = pendingResourceKey(userId);
    const next = readJsonArray(key).filter(
      (entry) => Number(entry?.resource_id) !== numericId
    );
    writeJson(key, next);
    return { result, queued: false, localOnly: false };
  } catch (error) {
    if (shouldKeepPending(error)) {
      queuePendingResourceState(userId, numericId, updates);
      return { result: null, queued: true, localOnly: false };
    }
    throw error;
  }
}

export async function flushPendingResourceStates(userId) {
  if (!userId || !isUserConnected()) {
    return { processed: 0, synced: 0, kept: 0, dropped: 0 };
  }

  const key = pendingResourceKey(userId);
  const pending = readJsonArray(key);
  const kept = [];
  let synced = 0;
  let dropped = 0;

  for (const entry of pending) {
    const numericId = Number(entry?.resource_id);
    if (
      !Number.isInteger(numericId) ||
      numericId <= 0 ||
      !entry?.updates ||
      typeof entry.updates !== 'object'
    ) {
      dropped += 1;
      continue;
    }

    try {
      await updateResourceState(numericId, entry.updates);
      synced += 1;
    } catch (error) {
      if (shouldKeepPending(error)) {
        kept.push({
          ...entry,
          attempts: Number(entry.attempts || 0) + 1,
          lastAttemptAt: new Date().toISOString(),
        });
      } else {
        dropped += 1;
      }
    }
  }

  writeJson(key, kept.slice(-100));

  return {
    processed: pending.length,
    synced,
    kept: kept.length,
    dropped,
  };
}

function normalizeHistoryEntries(key) {
  const history = readJsonArray(key);
  let changed = false;

  const normalized = history.map((entry) => {
    if (entry?.client_uuid) return entry;
    changed = true;
    return {
      ...entry,
      client_uuid: generateClientUUID(),
    };
  });

  if (changed) {
    writeJson(key, normalized);
  }

  return normalized;
}

function scopedEntries(history, userId) {
  if (userId) {
    return history.filter(
      (entry) => String(entry?.user_id || '') === String(userId)
    );
  }

  return history.filter((entry) => !entry?.user_id);
}

export function claimUnownedLocalProgress(userId) {
  if (!userId) return 0;

  const ownerKey = 'progress_legacy_owner_v1';
  const existingOwner = localStorage.getItem(ownerKey);

  // Legacy unscoped data can only be claimed by one account on this browser.
  // This avoids silently assigning the same historical data to another user.
  if (existingOwner && String(existingOwner) !== String(userId)) {
    return 0;
  }

  let claimed = 0;
  for (const key of ['quiz_history', 'simulation_history']) {
    const history = normalizeHistoryEntries(key);
    const next = [];

    for (const entry of history) {
      if (entry?.user_id) {
        next.push(entry);
      } else {
        claimed += 1;
        next.push({
          ...entry,
          user_id: String(userId),
        });
      }
    }

    writeJson(key, next);
  }

  const difficult = readJsonArray('quiz_difficult');
  const scopedDifficult = [];
  for (const entry of difficult) {
    if (entry?.user_id) {
      scopedDifficult.push(entry);
    } else {
      claimed += 1;
      scopedDifficult.push({
        ...entry,
        user_id: String(userId),
      });
    }
  }
  writeJson('quiz_difficult', scopedDifficult);

  // Legacy resource markers were global. Claim them once into the same
  // user-scoped browser cache, then clear the anonymous buckets.
  for (const baseKey of ['resource_favorites', 'resource_read']) {
    const legacyValues = readJsonArray(baseKey);
    if (legacyValues.length > 0) {
      const scopedKey = resourceLocalKey(baseKey, userId);
      const existingValues = readJsonArray(scopedKey);
      writeJson(scopedKey, Array.from(new Set([...existingValues, ...legacyValues])));
      writeJson(baseKey, []);
      claimed += legacyValues.length;
    }
  }

  if (claimed > 0 || !existingOwner) {
    localStorage.setItem(ownerKey, String(userId));
  }

  return claimed;
}

function toMigrationEntry(entry, activityType) {
  const parsedDate = new Date(entry.date || 0);
  const occurredAt = Number.isFinite(parsedDate.getTime())
    ? parsedDate.toISOString()
    : new Date(0).toISOString();

  return {
    client_uuid: entry.client_uuid,
    activity_type: activityType,
    title: entry.sujet || entry.titre || 'Session',
    subject: entry.matiere || (activityType === 'simulation' ? 'Simulation' : 'Autre'),
    category: entry.lot || '',
    score: Number(entry.score),
    total_questions: Number.isFinite(Number(entry.totalQuestions))
      ? Number(entry.totalQuestions)
      : null,
    correct_answers: Number.isFinite(Number(entry.correctAnswers))
      ? Number(entry.correctAnswers)
      : null,
    exam_mode: Boolean(entry.examMode),
    is_official: Boolean(entry.est_sujets_officiels),
    occurred_at: occurredAt,
  };
}

export function getLocalProgressEntries(userId) {
  const quizHistory = scopedEntries(
    normalizeHistoryEntries('quiz_history'),
    userId
  ).filter((entry) => entry?._serverCached !== true);
  const simulationHistory = scopedEntries(
    normalizeHistoryEntries('simulation_history'),
    userId
  ).filter((entry) => entry?._serverCached !== true);

  return [
    ...quizHistory.map((entry) => toMigrationEntry(entry, 'quiz')),
    ...simulationHistory.map((entry) => toMigrationEntry(entry, 'simulation')),
  ]
    .filter((entry) => Number.isFinite(entry.score))
    .sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at))
    .slice(0, 500);
}

function mapServerActivity(activity) {
  const activityType = activity.activity_type || 'quiz';
  return {
    date: activity.occurred_at,
    client_uuid: activity.client_uuid,
    sujet: activity.title || 'Session',
    titre: activity.title || 'Session',
    lot: activity.category || '',
    matiere:
      activity.subject ||
      (activityType === 'simulation' ? 'Simulation' : 'Autre'),
    score: Number(activity.score) || 0,
    totalQuestions: Number(activity.total_questions) || 0,
    correctAnswers: Number(activity.correct_answers) || 0,
    examMode: Boolean(activity.exam_mode),
    est_sujets_officiels: Boolean(activity.is_official),
    verified: Boolean(activity.verified),
    _serverCached: true,
    activityType,
  };
}

function buildLocalDashboard(userId = null) {
  const quizHistory = scopedEntries(
    normalizeHistoryEntries('quiz_history'),
    userId
  );
  const simulationHistory = scopedEntries(
    normalizeHistoryEntries('simulation_history'),
    userId
  );
  const difficultQuestions = scopedEntries(
    readJsonArray('quiz_difficult'),
    userId
  );
  const favorites = readJsonArray(resourceLocalKey('resource_favorites', userId));
  const readResources = readJsonArray(resourceLocalKey('resource_read', userId));

  const activities = [
    ...quizHistory.map((entry) => ({
      ...entry,
      activityType: 'quiz',
      verified: Boolean(entry.verified),
    })),
    ...simulationHistory.map((entry) => ({
      ...entry,
      activityType: 'simulation',
      verified: Boolean(entry.verified),
    })),
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const quizScores = quizHistory
    .map((entry) => Number(entry.score))
    .filter(Number.isFinite);
  const simulationScores = simulationHistory
    .map((entry) => Number(entry.score))
    .filter(Number.isFinite);
  const allScores = [...quizScores, ...simulationScores];

  const average = (scores) =>
    scores.length > 0
      ? scores.reduce((sum, value) => sum + value, 0) / scores.length
      : 0;

  const resourceIds = new Set([...favorites, ...readResources]);
  const resourceStates = [...resourceIds].map((resourceId) => ({
    resource_id: resourceId,
    is_favorite: favorites.includes(resourceId),
    is_read: readResources.includes(resourceId),
  }));

  return {
    summary: {
      totalQuizAttempts: quizHistory.length,
      totalSimulationAttempts: simulationHistory.length,
      totalAttempts: activities.length,
      quizAvgScore: average(quizScores),
      simAvgScore: average(simulationScores),
      averageScore: average(allScores),
      difficultQuestionsCount: difficultQuestions.length,
      favoriteResourcesCount: favorites.length,
      readResourcesCount: readResources.length,
    },
    activities,
    questionStates: [],
    resourceStates,
    generatedAt: null,
    source: 'local',
  };
}

function normalizeServerDashboard(rawDashboard) {
  const activities = Array.isArray(rawDashboard?.activities)
    ? rawDashboard.activities.map(mapServerActivity)
    : [];

  return {
    summary: rawDashboard?.summary || {
      totalQuizAttempts: 0,
      totalSimulationAttempts: 0,
      totalAttempts: 0,
      quizAvgScore: 0,
      simAvgScore: 0,
      averageScore: 0,
      difficultQuestionsCount: 0,
      favoriteResourcesCount: 0,
      readResourcesCount: 0,
    },
    activities,
    questionStates: Array.isArray(rawDashboard?.questionStates)
      ? rawDashboard.questionStates
      : [],
    resourceStates: Array.isArray(rawDashboard?.resourceStates)
      ? rawDashboard.resourceStates
      : [],
    generatedAt: rawDashboard?.generatedAt || new Date().toISOString(),
    source: 'server',
  };
}

function mergeDashboardWithLocal(
  baseDashboard,
  localDashboard,
  baseSource = 'cache',
  mergedSource = 'cache+local'
) {
  const cachedActivities = Array.isArray(baseDashboard?.activities)
    ? baseDashboard.activities
    : [];
  const localActivities = Array.isArray(localDashboard?.activities)
    ? localDashboard.activities
    : [];

  const seen = new Set(
    cachedActivities
      .map((activity) => activity?.client_uuid)
      .filter(Boolean)
  );

  const localOnly = localActivities.filter(
    (activity) =>
      activity?._serverCached !== true &&
      (!activity?.client_uuid || !seen.has(activity.client_uuid))
  );

  if (localOnly.length === 0) {
    return {
      ...baseDashboard,
      source: baseSource,
    };
  }

  const activities = [...cachedActivities, ...localOnly].sort(
    (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
  );
  const localQuiz = localOnly.filter(
    (activity) => activity.activityType === 'quiz'
  );
  const localSimulation = localOnly.filter(
    (activity) => activity.activityType === 'simulation'
  );

  const baseSummary = baseDashboard?.summary || {};
  const baseQuizCount = Number(baseSummary.totalQuizAttempts) || 0;
  const baseSimulationCount = Number(baseSummary.totalSimulationAttempts) || 0;
  const baseTotalCount =
    Number(baseSummary.totalAttempts) ||
    baseQuizCount + baseSimulationCount;

  const weightedAverage = (baseAverage, baseCount, additions) => {
    if (additions.length === 0) return Number(baseAverage) || 0;
    const additionScores = additions
      .map((activity) => Number(activity.score))
      .filter(Number.isFinite);
    const additionSum = additionScores.reduce((sum, score) => sum + score, 0);
    const totalCount = baseCount + additionScores.length;
    return totalCount > 0
      ? ((Number(baseAverage) || 0) * baseCount + additionSum) / totalCount
      : 0;
  };

  return {
    ...baseDashboard,
    activities,
    summary: {
      ...baseSummary,
      totalQuizAttempts: baseQuizCount + localQuiz.length,
      totalSimulationAttempts:
        baseSimulationCount + localSimulation.length,
      totalAttempts: baseTotalCount + localOnly.length,
      quizAvgScore: weightedAverage(
        baseSummary.quizAvgScore,
        baseQuizCount,
        localQuiz
      ),
      simAvgScore: weightedAverage(
        baseSummary.simAvgScore,
        baseSimulationCount,
        localSimulation
      ),
      averageScore: weightedAverage(
        baseSummary.averageScore,
        baseTotalCount,
        localOnly
      ),
      difficultQuestionsCount: Math.max(
        Number(baseSummary.difficultQuestionsCount) || 0,
        Number(localDashboard?.summary?.difficultQuestionsCount) || 0
      ),
      favoriteResourcesCount: Math.max(
        Number(baseSummary.favoriteResourcesCount) || 0,
        Number(localDashboard?.summary?.favoriteResourcesCount) || 0
      ),
      readResourcesCount: Math.max(
        Number(baseSummary.readResourcesCount) || 0,
        Number(localDashboard?.summary?.readResourcesCount) || 0
      ),
    },
    source: mergedSource,
  };
}

function refreshLocalCaches(dashboard, userId) {
  const preserveOtherUsers = (key) =>
    readJsonArray(key).filter(
      (entry) => String(entry?.user_id || '') !== String(userId)
    );

  const quizHistory = dashboard.activities
    .filter((activity) => activity.activityType === 'quiz')
    .map(({ activityType, ...entry }) => ({
      ...entry,
      user_id: String(userId),
    }));
  const simulationHistory = dashboard.activities
    .filter((activity) => activity.activityType === 'simulation')
    .map(({ activityType, ...entry }) => ({
      ...entry,
      user_id: String(userId),
    }));

  writeJson('quiz_history', [
    ...quizHistory,
    ...preserveOtherUsers('quiz_history'),
  ]);
  writeJson('simulation_history', [
    ...simulationHistory,
    ...preserveOtherUsers('simulation_history'),
  ]);

  // Keep server-backed numeric resource state scoped to the authenticated user,
  // while preserving virtual/local resource identifiers that PostgreSQL does
  // not model yet.
  const favoriteKey = resourceLocalKey('resource_favorites', userId);
  const readKey = resourceLocalKey('resource_read', userId);
  const localFavoriteVirtualIds = readJsonArray(favoriteKey).filter(
    (id) => !Number.isInteger(Number(id))
  );
  const localReadVirtualIds = readJsonArray(readKey).filter(
    (id) => !Number.isInteger(Number(id))
  );

  const favoriteIds = new Set(
    dashboard.resourceStates
      .filter((state) => state.is_favorite)
      .map((state) => state.resource_id)
  );
  const readIds = new Set(
    dashboard.resourceStates
      .filter((state) => state.is_read)
      .map((state) => state.resource_id)
  );

  // If the resource write endpoint is temporarily unavailable while the
  // dashboard endpoint still responds, do not overwrite the user's pending
  // local choice with stale server state.
  for (const pending of readJsonArray(pendingResourceKey(userId))) {
    const resourceId = Number(pending?.resource_id);
    if (!Number.isInteger(resourceId) || resourceId <= 0) continue;

    if (pending.updates?.is_favorite === true) favoriteIds.add(resourceId);
    if (pending.updates?.is_favorite === false) favoriteIds.delete(resourceId);
    if (pending.updates?.is_read === true) readIds.add(resourceId);
    if (pending.updates?.is_read === false) readIds.delete(resourceId);
  }

  writeJson(
    favoriteKey,
    Array.from(new Set([...localFavoriteVirtualIds, ...favoriteIds]))
  );
  writeJson(
    readKey,
    Array.from(new Set([...localReadVirtualIds, ...readIds]))
  );
  writeJson(`resource_state_cache_v1:${userId}`, dashboard.resourceStates);
}

export async function migrateLocalProgress(userId) {
  if (!userId || !isUserConnected()) {
    return { skipped: true, reason: 'anonymous' };
  }

  const entries = getLocalProgressEntries(userId);
  const migratedUuids = new Set(readJsonArray(migratedUuidsKey(userId)));
  const pendingEntries = entries.filter(
    (entry) => !migratedUuids.has(entry.client_uuid)
  );

  if (pendingEntries.length === 0) {
    return {
      received: 0,
      accepted: 0,
      rejected: 0,
      imported: 0,
      reconciled: 0,
      skipped: true,
      reason: 'already-migrated',
    };
  }

  const result = await importLocalHistory(pendingEntries);

  if (
    result?.accepted === pendingEntries.length &&
    Number(result?.rejected || 0) === 0
  ) {
    for (const entry of pendingEntries) {
      migratedUuids.add(entry.client_uuid);
    }

    writeJson(
      migratedUuidsKey(userId),
      Array.from(migratedUuids).slice(-1000)
    );
  }

  return result;
}

async function flushPendingHistoryClear(userId) {
  if (!userId || !isUserConnected()) return false;

  const key = pendingClearKey(userId);
  const pending = readJson(key, null);
  if (!pending?.quiz) return false;

  await clearQuizHistoryApi();
  localStorage.removeItem(key);
  localStorage.removeItem(cacheKey(userId));
  return true;
}

async function fetchServerPrimaryDashboard(userId, limit) {
  try {
    // A clear request has priority: never let an old server response resurrect
    // history the user already cleared while temporarily offline.
    await flushPendingHistoryClear(userId);

    // Full attempt payloads and resource state changes are retried before
    // fetching the authoritative dashboard.
    await flushPendingAttempts(userId);
    await flushPendingResourceStates(userId);

    try {
      await migrateLocalProgress(userId);
    } catch (migrationError) {
      // A legacy migration issue must never prevent reading already-synced
      // server progress.
      console.warn(
        'Migration de la progression locale différée.',
        migrationError
      );
    }

    const rawDashboard = await getProgressDashboard(limit);
    const serverDashboard = normalizeServerDashboard(rawDashboard);
    const dashboard = mergeDashboardWithLocal(
      serverDashboard,
      buildLocalDashboard(userId),
      'server',
      'server+local'
    );

    const legacyDifficultCount = scopedEntries(
      readJsonArray('quiz_difficult'),
      userId
    ).length;
    dashboard.summary.difficultQuestionsCount = Math.max(
      Number(dashboard.summary.difficultQuestionsCount) || 0,
      legacyDifficultCount
    );

    writeJson(cacheKey(userId), {
      userId: String(userId),
      cachedAt: new Date().toISOString(),
      dashboard,
    });
    refreshLocalCaches(dashboard, userId);

    return dashboard;
  } catch (error) {
    const localDashboard = buildLocalDashboard(userId);
    const cached = readJson(cacheKey(userId), null);

    if (
      cached?.dashboard &&
      String(cached.userId) === String(userId)
    ) {
      return mergeDashboardWithLocal(
        cached.dashboard,
        localDashboard,
        'cache',
        'cache+local'
      );
    }

    return localDashboard;
  }
}

export function loadServerPrimaryDashboard(userId, limit = 100) {
  if (!userId || !isUserConnected()) {
    return Promise.resolve(buildLocalDashboard(userId));
  }

  const key = `${userId}:${limit}`;
  if (dashboardLoads.has(key)) {
    return dashboardLoads.get(key);
  }

  const request = fetchServerPrimaryDashboard(userId, limit).finally(() => {
    dashboardLoads.delete(key);
  });

  dashboardLoads.set(key, request);
  return request;
}

export function getCachedProgressDashboard(userId) {
  const cached = readJson(cacheKey(userId), null);
  if (
    cached?.dashboard &&
    String(cached.userId) === String(userId)
  ) {
    return {
      ...cached.dashboard,
      source: 'cache',
    };
  }

  return buildLocalDashboard(userId);
}

export async function clearServerPrimaryQuizHistory(userId) {
  const currentId = userId ? String(userId) : null;
  const allQuizHistory = readJsonArray('quiz_history');
  const remaining = allQuizHistory.filter((entry) =>
    currentId
      ? String(entry?.user_id || '') !== currentId
      : Boolean(entry?.user_id)
  );

  writeJson('quiz_history', remaining);

  if (!currentId || !isUserConnected()) {
    return { cleared: true, source: 'local' };
  }

  // Persist the user's intent before touching the network.
  writeJson(pendingClearKey(currentId), {
    quiz: true,
    requestedAt: new Date().toISOString(),
  });

  // Never allow an older dashboard cache to resurrect the cleared history.
  localStorage.removeItem(cacheKey(currentId));

  // Pending quiz attempts are older than the clear cutoff that will be written.
  // Dropping them avoids needless uploads while leaving simulation retries intact.
  const attemptsKey = pendingAttemptsKey(currentId);
  const pending = readJsonArray(attemptsKey).filter(
    (entry) => entry?.type !== 'quiz'
  );
  writeJson(attemptsKey, pending);

  const result = await clearQuizHistoryApi();
  localStorage.removeItem(pendingClearKey(currentId));

  return {
    ...result,
    source: 'server',
  };
}
